import React, { CSSProperties, forwardRef, ReactNode, useEffect, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';

interface ComponentProps {
  id: number;
  data: any;
  children: ReactNode;
  style?: CSSProperties;
  dragPlaceholder?: ReactNode;
  renderChildrenWhileDragging?: boolean;
  disabled?: boolean;
}

const DraggableItem = forwardRef<HTMLDivElement, ComponentProps>(
  ({ id, data, children, style, dragPlaceholder, renderChildrenWhileDragging = true, disabled = false }, ref) => {
    const elementRef = useRef<HTMLDivElement | null>(null);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id,
      data,
      disabled,
    });

    // Function to capture computed styles of the element
    useEffect(() => {
      if (elementRef.current) {
        data.children = children;
      }
    }, [elementRef, data, children]);

    const dragStyle = {
      ...style,
      opacity: isDragging ? 0.5 : 1,
    };

    const renderContent = () => {
      if (!isDragging) {
        return children;
      }

      // While dragging, choose what to render based on props
      if (renderChildrenWhileDragging) {
        return children;
      } else if (dragPlaceholder) {
        return dragPlaceholder;
      } else {
        return <div className="draggable-item-placeholder"></div>;
      }
    };

    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          elementRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        style={dragStyle}
        className={`draggable-item ${disabled ? 'drag-disabled' : ''}`}
      >
        {renderContent()}
      </div>
    );
  },
);

DraggableItem.displayName = 'DraggableItem';
export default DraggableItem;
